import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ApiService } from './api/api.service';

export enum FileStorage {
    TinyImages = 'tinyimages',
    ToolBox = 'toolbox',
    Tickets = 'tickets',
    Planning = 'planning',
    Checklists = 'checklists',
    ProfilePictures = 'profilepictures',
}

@Injectable({ providedIn: 'root' })
export class FileService {
    static readonly MAX_IMAGE_WIDTH = 1920;
    static readonly MAX_IMAGE_HEIGHT = 1080;
    static readonly IMAGE_QUALITY = 0.9;
    constructor(private apiService: ApiService) {}

    upload(file: File, storage = FileStorage.TinyImages) {
        return this.apiService.makeFileRequest$<{
            file: string;
            kind: string;
        }>('file/upload', file, { storage });
    }

    resizeAndUpload(file: File, storage = FileStorage.TinyImages): Observable<{ file: string; kind: string }> {
        return new Observable(observer => {
            this.resizeImage(
                file,
                FileService.MAX_IMAGE_WIDTH,
                FileService.MAX_IMAGE_HEIGHT,
                FileService.IMAGE_QUALITY
            ).then(resized => {
                this.upload(resized, storage)
                    .pipe(
                        switchMap(response => {
                            observer.next(response.data);
                            observer.complete();
                            return [];
                        })
                    )
                    .subscribe({ error: err => observer.error(err) });
            });
        });
    }

    uploadPublic(file: File): Observable<{ file: string; kind: string }> {
        return this.apiService
            .makeFileRequest$<{ file: string; kind: string }>('tickets/public/upload', file)
            .pipe(map(response => response.data));
    }

    resizeAndUploadPublic(file: File): Observable<{ file: string; kind: string }> {
        return new Observable(observer => {
            this.resizeImage(
                file,
                FileService.MAX_IMAGE_WIDTH,
                FileService.MAX_IMAGE_HEIGHT,
                FileService.IMAGE_QUALITY
            ).then(resized => {
                this.uploadPublic(resized).subscribe({
                    next: response => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: err => observer.error(err),
                });
            });
        });
    }

    resizeImage(file: File, maxWidth: number, maxHeight: number, quality: number): Promise<File> {
        return new Promise(resolve => {
            const img = new Image();
            const url = URL.createObjectURL(file);
            img.onload = () => {
                URL.revokeObjectURL(url);
                let { width, height } = img;
                if (width <= maxWidth && height <= maxHeight) {
                    resolve(file);
                    return;
                }
                const ratio = Math.min(maxWidth / width, maxHeight / height);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                canvas.getContext('2d').drawImage(img, 0, 0, width, height);

                const mimeType = file.type || 'image/jpeg';
                canvas.toBlob(
                    blob => {
                        resolve(new File([blob], file.name, { type: mimeType }));
                    },
                    mimeType,
                    quality
                );
            };
            img.src = url;
        });
    }
}
